import React, { useEffect, useRef, useState } from "react";
import classes from "./Leaderboard.module.css";

import { RxCounterClockwiseClock } from "react-icons/rx";
import SearchInput from "../../ui/SearchInput";
import CrownIcon from "../../icon/CrownIcon";
import MedalIcon from "../../icon/MedalIcon";
import HonourStarIcon from "../../icon/HonourStarIcon";
import { IoChevronDown } from "react-icons/io5";
import { LuCheck, LuRefreshCcw } from "react-icons/lu";
import { FiSearch } from "react-icons/fi";
import useWindowDimensions from "../../hooks/getWindowDimensions";
import StepToggle from "../../ui/StepToggle/StepToggle";

const months = [
  "August",
  "September",
  "October",
  "November", 
  "December", 
  "January - 2026", 
  "February - 2026",
  "March - 2026",
  "April - 2026",
  "May - 2026",
  "June - 2026", // Current month
  "July - 2026",
  "August - 2026",
  "September - 2026",
];

const Leaderboard = ({
  title,
  blurred = false,
  search,
  allFillerRows,
  baseData,
  setShowHistory,
  selectedMonth,
  setSelectedMonth,
  isSpinning,
  setIsSpinning,
  fetchLeaderboard,
  loading,
  setSelected,
  selected,
}) => {
  // const [isMobile, setIsMobile] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { width } = useWindowDimensions();

  const isMobile = width <= 768; // Mobile breakpoint

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectMonth = (month) => {
    setSelectedMonth(month);
    setDropdownOpen(false);
  };

  // useEffect(() => {
  //   const handleResize = () => {
  //     setIsMobile(window.innerWidth <= 768); // Mobile breakpoint
  //   };

  //   handleResize(); // Run on mount
  //   window.addEventListener("resize", handleResize);

  //   return () => window.removeEventListener("resize", handleResize);
  // }, []);

  const rawData = [
    ...baseData,
    ...(isMobile ? allFillerRows.slice(0, 3) : allFillerRows),
  ];

  // Apply blur if the `blurred` prop is true — affect ALL rows
  const leaderboardData = blurred
    ? rawData.map((row) => ({ ...row, blurred: true }))
    : rawData;

  // Filter leaderboardData based on user name
  const filteredData = leaderboardData.filter((entry) => {
    if (entry.isDivider) return searchQuery === ""; // hide divider if searching
    return entry.user?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className={`${classes.wrapper} position-relative`}>
      {blurred && (
        <div className={classes.overlay}>
          We are fixing a bug, check back later
        </div>
      )}
      <div className={blurred ? classes.blurContent : ""}>
        <div
          className={`d-flex align-items-center justify-content-between gap-3 mb-4 ${classes.headingContainer}`}
        >
          {mobileSearchOpen ? (
            <SearchInput
              showClose
              setisOpen={setMobileSearchOpen}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search user"
            />
          ) : (
            <>
              <div className={classes.dropdownWrapper} ref={dropdownRef}>
                <h5
                  className="text-white"
                  style={{ cursor: "pointer" }}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  {selectedMonth}
                  <IoChevronDown size={18} /> Challenge
                </h5>

                {dropdownOpen && (
                  <ul className={classes.dropdownMenu}>
                    {months.map((month) => (
                      <li
                        key={month}
                        className={`d-flex align-items-center justify-content-between ${
                          classes.dropdownItem
                        } ${month === selectedMonth ? classes.activeItem : ""}`}
                        onClick={() => handleSelectMonth(month)}
                      >
                        <>
                          {month} {month === "September - 2026" && "(Current)"}
                        </>{" "}
                        {month === selectedMonth && <LuCheck />}
                      </li>
                    ))}
                  </ul>
                )}
                {/* Move StepToggle below dropdown ONLY on mobile */}
                {isMobile && (
                  <div className="mt-2">
                    <StepToggle
                      options={["1-Step", "2-Step"]}
                      value={selected}
                      onChange={(v) => setSelected(v)}
                      circular
                    />
                  </div>
                )}
              </div>

              {/* Desktop StepToggle stays in original place */}
              {!isMobile && (
                <StepToggle
                  options={["1-Step", "2-Step"]}
                  value={selected}
                  onChange={(v) => setSelected(v)}
                  circular
                />
              )}

              {search && !isMobile && (
                <SearchInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search user"
                />
              )}
              {isMobile ? (
                <div className="d-flex align-items-center gap-4">
                  <LuRefreshCcw
                    onClick={() => {
                      setIsSpinning(true);
                      fetchLeaderboard();
                    }}
                    className={`${classes.refreshIcon} ${
                      isSpinning ? classes.spinning : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  />
                  <FiSearch
                    className={classes.searchIcon}
                    onClick={() => setMobileSearchOpen(true)}
                  />
                  <RxCounterClockwiseClock
                    color="var(--primary-color)"
                    size={18}
                    onClick={() => setShowHistory(true)}
                  />
                </div>
              ) : (
                <div className="d-flex align-items-center gap-4">
                  <div className="d-flex align-items-center gap-2">
                    <LuRefreshCcw
                      onClick={() => {
                        setIsSpinning(true);
                        fetchLeaderboard();
                      }}
                      className={`${classes.refreshIcon} ${
                        isSpinning ? classes.spinning : ""
                      }`}
                      style={{ cursor: "pointer" }}
                    />
                    <h6 className={classes.realTimeText}>
                      Updates upon refresh
                    </h6>
                  </div>
                  <button
                    onClick={() => setShowHistory(true)}
                    className={`d-flex align-items-center gap-1 ${classes.historyButton}`}
                  >
                    <RxCounterClockwiseClock
                      color="var(--primary-color)"
                      size={15}
                    />
                    History
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="table-responsive">
          <table className={classes.table}>
            <thead>
              <tr>
                <th>RANK</th>
                <th>USER</th>
                <th>{isMobile ? "EQUITY" : "ACCOUNT EQUITY"}</th>
                {!isMobile && (
                  <>
                    <th>STAGE</th>
                    <th>STATUS</th>
                    <th>MAX DRAWDOWN</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((entry) => {
                if (entry.isDivider && searchQuery === "") {
                  return (
                    <tr
                      key="outside-ring"
                      className={`${classes.dividerRow} ${classes.darkRow}`}
                    >
                      <td
                        colSpan="6"
                        className="text-center text-white fw-bold py-3"
                      >
                        <div className={classes.hrWrapper}>
                          <hr className={classes.hrLine} />
                          <span className={classes.outsideBorder}>
                            <span className={classes.outsideText}>
                              {entry.label}
                            </span>
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                }
                const rowClass = "";

                const icon =
                  entry.rank === 1 ? (
                    <CrownIcon />
                  ) : entry.rank === 2 ? (
                    <MedalIcon />
                  ) : entry.rank === 3 ? (
                    <HonourStarIcon />
                  ) : null;

                const returnClass =
                  entry.status === "Passed"
                    ? classes.returnPositive
                    : entry.status === "In progress"
                    ? classes.returnProgress
                    : entry.status === "Failed"
                    ? classes.returnNegative
                    : "";

                const drawdownClass =
                  entry.drawdown > 0
                    ? classes.returnPositive
                    : classes.returnNegative;

                return (
                  <tr
                    key={entry.rank}
                    className={`${rowClass} ${
                      entry.blurred
                        ? classes.blurredRow
                        : entry.dark
                        ? classes.darkRow
                        : ""
                    }`}
                  >
                    <td
                      className={`${classes.rankCell} ${
                        entry.rank === 1
                          ? classes.top1
                          : entry.rank === 2
                          ? classes.top2
                          : entry.rank === 3
                          ? classes.top3
                          : classes.bottomRank
                      }`}
                    >
                      <div className="d-flex align-items-center gap-2">
                        {icon}
                        {entry.rank}
                      </div>
                    </td>
                    <td>
                      {entry.user}
                      {isMobile && (
                        <div className={classes.stageStatus}>
                          Stage: 1, Status:{" "}
                          <span className={returnClass}>In Progress</span>
                        </div>
                      )}
                    </td>
                    <td>{entry.equity}</td>
                    {!isMobile && (
                      <>
                        <td>{entry.stage}</td>
                        <td className={returnClass}>{entry.status}</td>
                        <td
                          className={drawdownClass}
                          style={{ paddingLeft: "95px" }}
                        >
                          {entry.drawdown}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
